<script>
import MetricCard from "../components/MetricCard.vue";
import Table from "../components/Table.vue";
import SearchBar from "../components/SearchBar.vue";
import Icon from "../components/Icon.vue";
import FilterDropdown from "../components/FilterDropdown.vue";
import LineChart from "../components/charts/LineChart.vue";
import Loader from "../components/Loader.vue";
import RamroPagination from "@/components/Pagination/Pagination.vue";
import { mapState, mapActions } from "vuex";

export default {
    name: "featureUsage",
    components: {
        MetricCard,
        Table,
        SearchBar,
        Icon,
        FilterDropdown,
        LineChart,
        Loader,
        RamroPagination,
    },
    data() {
        return {
            columns: [
                { key: 'feature_name', header: 'Feature Name', sortable: true, render: ({ row }) => row.feature_name.split('_').join(' ') },
                { 
                    key: 'tenants_using', 
                    header: 'Tenants Using', 
                    sortable: true,
                    render: ({ row }) => `${row.tenants_using}<span class="text-green-800 px-3 py-2 rounded text-sm bg-green-100 ml-2">${row.tenant_percentage}%</span>`
                },
                {
                    key: 'total_users_using', 
                    header: 'Users Using', 
                    sortable: true,
                    render: ({ row }) => `${row.total_users_using}<span class="text-green-800 px-3 py-2 rounded text-sm bg-green-100 ml-2">${row.user_percentage}%</span>`
                },
                { key: 'total_usage', header: 'Total Usage', sortable: true },
                { key: 'average_monthly_usage', header: 'Average Monthly Usage', sortable: true, render: ({ row }) => row.average_monthly_usage.toFixed(2) },
                { key: 'last_used_date', header: 'Last Used Date', sortable: true, render: ({ row }) => 
                {
                    const date = row && row.last_used_date;
                    if (typeof date === 'string' && date.includes(' ')) {
                        return date.split(' ')[0];
                    }
                    return typeof date === 'string' ? date : (date ? String(date) : '-');
                } }
            ],
            loading: false,
            chartLoading: true,
            searchQuery: '',
            chartSeries: [],
            chartColors: ['#8979FF', '#FF928A', '#3CC3DF', '#FFAE4C', '#0398DC'],
            currentPage: 1,
            perPage: 10,
            exportLoading: false,
            tenantLoading: true,
            recentTenantLoading: true,
            recentFeatureLoading: true,
            riskyTenantLoading: true,
            riskyFeatureLoading: true,
            overallFeatureLoading: true,
            selectedPlan: null,
            metricConfigs: [
                { key: 'total_features', label: "Total Features", icon: "BoxIcon", iconColor: "blue", format: 'integer' },
                { key: 'total_active_tenants', label: "Total Active Tenants", icon: "PeopleIcon", iconColor: "blue", format: 'integer' },
                { key: 'total_active_users', label: "Total Active Users", icon: "PeopleIcon", iconColor: "blue", format: 'integer' },
                { key: 'total_usage', label: "Total Usage", icon: "TrendIcon", iconColor: "blue", format: 'decimal' },
                { key: 'average_monthly_usage', label: "Average Monthly Usage", icon: "RiseIcon", iconColor: "blue", format: 'decimal' },
            ],
        }
    },
    computed: {
        ...mapState('featureUsage', [
            'allTenantUsage',
            'allTenantUsagePagination',
            'topFeaturesMonthly',
            'topTenants',
            'recentTenants',
            'recentFeatures',
            'riskyTenants',
            'riskyFeatures',
            'overallFeatureCount'
        ]),
        pagination() {
            return this.allTenantUsagePagination || { current_page: 1, per_page: 10, total: 0, total_pages: 1 };
        },
        tableRows() {
            return this.allTenantUsage || [];
        },
        dataRefreshRange() {
            return this.overallFeatureCount?.last_updated && this.overallFeatureCount?.last_updated.split(' ')[0] || 'Last 1 week';
        },
        metrics() {
            return this.metricConfigs.map(config => {
                const rawValue = this.overallFeatureCount?.[config.key];
                let value;
                if (config.format === 'decimal') {
                    value = rawValue ? Number(rawValue).toFixed(2) : '0.00';
                } else {
                    value = rawValue ? Number(rawValue) : '0';
                }
                return {
                    label: config.label,
                    value,
                    icon: config.icon,
                    iconColor: config.iconColor,
                    compareValue: '',
                };
            });
        },
        filteredRows() {
            return this.tableRows.filter(row => 
                row.feature_name && row.feature_name.toLowerCase().includes(this.searchQuery.toLowerCase())
            );
        },
        dynamicChartSeries() {
            return this.topFeaturesMonthly.map((feature, index) => ({
                name: feature.feature_name,
                data: Object.values(feature.monthly_data),
                color: this.chartColors[index % this.chartColors.length]
            }));
        },
        filterItems() {
            const features = this.topFeaturesMonthly && Array.isArray(this.topFeaturesMonthly) && this.topFeaturesMonthly.length > 0 
                ? this.topFeaturesMonthly.map(item => ({id: item.id, title: item.feature_name, label: item.feature_name && item.feature_name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')})) 
                : [];
            return [{id: 1, title: 'All Features'}, ...features];
        },
        featureColumns() {
            return [
                { key: 'feature_name', header: 'Feature Name', sortable: true },
                { 
                    key: 'tenants_using', 
                    header: 'Tenants Using', 
                    sortable: true,
                    render: ({ row }) => `${row.tenants_using}<span class="text-green-800 px-3 py-2 rounded text-sm bg-green-100 ml-2">${row.tenant_percentage}%</span>`
                },
                { 
                    key: 'total_users_using', 
                    header: 'Users Using', 
                    sortable: true,
                    render: ({ row }) => `${row.total_users_using}<span class="text-green-800 px-3 py-2 rounded text-sm bg-green-100 ml-2">${row.user_percentage}%</span>`
                },
                { key: 'total_usage', header: 'Total Usage', sortable: true },
                { key: 'average_monthly_usage', header: 'Average Monthly Usage', sortable: true, render: ({ row }) => row.average_monthly_usage },
                { 
                    key: 'last_used_date', 
                    header: 'Last Used Date', 
                    sortable: true,
                    render: ({ row }) => {
                        const date = row && row.last_used_date;
                        if (date && typeof date === 'string' && date.includes(' ')) {
                            return date.split(' ')[0];
                        }
                        return date && typeof date === 'string' ? date : (date ? String(date) : '-');
                    }
                }
            ];
        },
        chartCategories() {
            if (this.topFeaturesMonthly && this.topFeaturesMonthly.length > 0) {
                return Object.keys(this.topFeaturesMonthly[0].monthly_data);
            }
            return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        },
        tenantColumns() {
            return [
                { key: 'subdomain', header: 'Tenant Name', sortable: true },
                { key: 'business_name', header: 'Business Name', sortable: true },
                { key: 'plan', header: 'Plan', sortable: true,
                    render: this.getPlanRender
                },
                { key: 'total_users', header: 'User Count', sortable: true },
                { key: 'total_branches', header: 'Branch Count', sortable: true },
                { key: 'total_usage', header: 'Total Usage', sortable: true },
                { key: 'average_monthly_usage', header: 'Average Monthly Usage', sortable: true, render: ({ row }) => row.average_monthly_usage },
                { key: 'last_updated', header: 'Last Active', sortable: true, render: ({ row }) => 
                {
                    const date = row && row.last_updated;
                    if (typeof date === 'string' && date.includes(' ')) {
                        return date.split(' ')[0];
                    }
                    return typeof date === 'string' ? date : (date ? String(date) : '-');
                } },
                { 
                    key: 'most_used_feature', 
                    header: 'Most Used Feature', 
                    sortable: true,
                    render: this.getMostUsedFeatureRender
                },
                { 
                    key: 'least_used_feature', 
                    header: 'Least Used Feature', 
                    sortable: true,
                    render: this.getLeastUsedFeatureRender
                },
            ];
        },
        recentTenantColumns() {
            return [
                { key: 'subdomain', header: 'Tenant Name', sortable: true },
                { key: 'business_name', header: 'Business Name', sortable: true },
                { key: 'plan', header: 'Plan', sortable: true,
                    render: this.getPlanRender
                },
                { key: 'total_users', header: 'User Count', sortable: true },
                { key: 'total_branches', header: 'Branch Count', sortable: true },
                { key: 'total_usage', header: 'Total Usage', sortable: true },
                { key: 'average_monthly_usage', header: 'Average Monthly Usage', sortable: true, render: ({ row }) => row.average_monthly_usage },
                { key: 'last_updated', header: 'Last Active', sortable: true, render: ({ row }) => 
                {
                    const date = row && row.last_updated;
                    if (typeof date === 'string' && date.includes(' ')) {
                        return date.split(' ')[0];
                    }
                    return typeof date === 'string' ? date : (date ? String(date) : '-');
                } },
                { 
                    key: 'most_used_features', 
                    header: 'Most Used Features', 
                    sortable: true,
                    render: this.getMostUsedFeatureRender
                },
                { 
                    key: 'least_used_features', 
                    header: 'Least Used Features', 
                    sortable: true,
                    render: this.getLeastUsedFeatureRender
                },
            ];
        },
        riskyTenantColumns() {
            return [
                { key: 'subdomain', header: 'Tenant Name', sortable: true },
                { key: 'last_month_usage', header: 'Last Month Usage', sortable: true },
                { key: 'this_month_usage', header: 'This Month Usage', sortable: true },
                { key: 'usage_decline_rate', header: 'Usage Decline Rate', sortable: true, render: this.getUsageDeclineRender },
            ];
        },
        riskyFeatureColumns() {
            return [
                { key: 'feature_name', header: 'Feature Name', sortable: true },
                { key: 'last_month_usage', header: 'Last Month Usage', sortable: true },
                { key: 'this_month_usage', header: 'This Month Usage', sortable: true },
                { key: 'usage_decline_rate', header: 'Usage Decline Rate', sortable: true, render: this.getUsageDeclineRender },
            ];
        }
    },
    watch: {
        topFeaturesMonthly(newVal) {
            if (newVal && newVal.length > 0) {
                this.chartSeries = this.dynamicChartSeries;
            }
        }
    },
    mounted() {
        this.refetchAllData();
    },
    methods: {
        ...mapActions('featureUsage', [
            'fetchAllTenantUsage',
            'downloadAllTenantUsageCsv',
            'fetchTopFeaturesMonthly',
            'fetchTopTenants',
            'downloadTopTenantsCsv',
            'fetchRecentTenants',
            'downloadRecentTenantsCsv',
            'fetchRecentFeatures',
            'downloadRecentFeaturesCsv',
            'fetchRiskyTenants',
            'downloadRiskyTenantsCsv',
            'fetchRiskyFeatures',
            'downloadRiskyFeaturesCsv',
            'fetchOverallFeatureCount'
        ]),
        handleRowClick({ row }) {
            const feature = row && row.feature_name ? row.feature_name : null;
            if (!feature) return;
            this.$router.push({ name: 'individualFeatureUsage', params: { feature } });
        },
        getCompareValueColor(metric) {
            return metric && metric.compareValue && metric.compareValue.startsWith('+') ? 'green' : 'red';
        },
        searchAllFeatures(value) {
            this.searchQuery = value;
            this.currentPage = 1;
            this.fetchFeaturePage();
        },
        async fetchFeaturePage() {
            this.loading = true;
            await this.fetchAllTenantUsage({ plan: this.selectedPlan, page: this.currentPage, per_page: this.perPage });
            this.loading = false;
        },
        handlePageChange({ page }) {
            this.currentPage = page;
            this.fetchFeaturePage();
        },
        exportCSV(type) {
            this.exportLoading = true;
            if (type === 'allFeatures') {
                this.downloadAllTenantUsageCsv().finally(() => {
                    this.exportLoading = false;
                });
            } else if (type === 'top10ActiveTenants') {
                this.downloadTopTenantsCsv().finally(() => {
                    this.exportLoading = false;
                });
            } else if (type === 'top10RecentTenants') {
                this.downloadRecentTenantsCsv().finally(() => {
                    this.exportLoading = false;
                });
            } else if (type === 'top10RecentFeatures') {
                this.downloadRecentFeaturesCsv().finally(() => {
                    this.exportLoading = false;
                });
            } else if (type === 'top10RiskyTenants') {
                this.downloadRiskyTenantsCsv().finally(() => {
                    this.exportLoading = false;
                });
            } else if (type === 'top10RiskyFeatures') {
                this.downloadRiskyFeaturesCsv().finally(() => {
                    this.exportLoading = false;
                });
            }
        },
        selectPlan(value) {
            if (value.id === 'all') {
                this.selectedPlan = null;
            } else {
                this.selectedPlan = value ? value.title.toLowerCase() : null;
            }
            this.currentPage = 1;
            this.refetchAllData();
        },
        async refetchAllData() {
            this.loading = true;
            this.chartLoading = true;
            this.tenantLoading = true;
            this.recentTenantLoading = true;
            this.recentFeatureLoading = true;
            this.riskyTenantLoading = true;
            this.riskyFeatureLoading = true;
            this.overallFeatureLoading = true;
            
            await this.fetchOverallFeatureCount(this.selectedPlan);
            this.overallFeatureLoading = false;
            
            await this.fetchAllTenantUsage({ plan: this.selectedPlan, page: this.currentPage, per_page: this.perPage });
            this.loading = false;
            
            await this.fetchTopFeaturesMonthly(this.selectedPlan);
            this.chartLoading = false;
            
            await this.fetchTopTenants(this.selectedPlan);
            this.tenantLoading = false;
            
            await this.fetchRecentTenants(this.selectedPlan);
            this.recentTenantLoading = false;
            
            await this.fetchRecentFeatures(this.selectedPlan);
            this.recentFeatureLoading = false;
            
            await this.fetchRiskyTenants(this.selectedPlan);
            this.riskyTenantLoading = false;
            
            await this.fetchRiskyFeatures(this.selectedPlan);
            this.riskyFeatureLoading = false;
        },
        selectFeature(value) {
            if (value.title === 'All Features') {
                this.chartSeries = [...this.dynamicChartSeries];
            } else {
                this.chartSeries = this.dynamicChartSeries.filter(item => item.name === value.title);
            }
        },

        getPlanColor(plan) {
            if (!plan || typeof plan !== 'string') return 'gray';
            if (plan.toLowerCase().includes('starter')) {
                return 'yellow';
            }
            if (plan.toLowerCase().includes('professional')) {
                return 'blue';
            }
            if (plan.toLowerCase().includes('premium')) {
                return 'green';
            }
            return 'gray';
        },
        getPlanRender({ row }) {
            return { component: 'Capsule', props: { values: [row && row.plan && typeof row.plan === 'string' && row.plan.split('-')[0] || row.plan || 'Unknown'], color: this.getPlanColor(row.plan) } };
        },
        getMostUsedFeatureRender({ row }) {
            const feature = row.most_used_features || row.most_used_feature;
            if (!feature) return { component: 'Tag', props: { tags: [], color: 'gray' } };
            const tags = Array.isArray(feature) ? feature.map(f => f && f.name || '').filter(name => name) : [feature.name || ''];
            return { component: 'Tag', props: { tags, color: 'gray' } };
        },
        getLeastUsedFeatureRender({ row }) {
            const feature = row.least_used_features || row.least_used_feature;
            if (!feature) return { component: 'Tag', props: { tags: [], color: 'gray' } };
            const tags = Array.isArray(feature) ? feature.map(f => f && f.name || '').filter(name => name) : [feature.name || ''];
            return { component: 'Tag', props: { tags, color: 'gray' } };
        },
        getUsageDeclineRender({ row }) {
            return `<span class="text-red-800 px-3 py-2 rounded text-sm bg-red-100 ml-2">${row.usage_decline_rate}%</span>`;
        },
        formatLastUsedDate(date) {
            if (!date) return '-';
            if (typeof date === 'string' && date.includes(' ')) {
                return date.split(' ')[0];
            }
            return typeof date === 'string' ? date : String(date);
        },
    }
}
</script>

<template>
    <div class="flex flex-col gap-8 p-6">
        <div class="flex items-center justify-between">
            <div>
                <h1 class="text-3xl font-bold text-gray-900"> Feature Usage Dashboard </h1>
                <p class="mt-1 text-sm text-gray-600"> Monitor feature adoption and usage across all tenants </p>
            </div>
            <div class="flex items-center space-x-3">
                <div class="px-5 py-4 border border-blue-600 rounded-md text-blue-600 flex items-center gap-3 font-bold h-fit focus:border-blue-600">
                    <Icon icon-name="CalendarIcon" />
                    <div>
                        {{ dataRefreshRange }}
                    </div>
                </div>
                <FilterDropdown
                    :items="[{id: 'all', title: 'All'}, {id: 1, title: 'Starter'}, { id: 2, title: 'Standard'}, {id: 3, title: 'Professional'}, {id: 4, title: 'Premium'}]"
                    label="Plan"
                    iconName="StarIcon"
                    @select="selectPlan"
                />
            </div>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <MetricCard 
                v-for="(metric, index) in metrics" 
                :key="index"
                :label="metric.label"
                :value="metric.value"
                :icon="metric.icon"
                :icon-color="metric.iconColor"
                :compare-value="metric.compareValue"
                :compare-value-color="getCompareValueColor(metric)"
            />
        </div>
        <metric-card class="flex flex-col gap-5">
            <div class="flex justify-between items-center">
                <SearchBar
                    @update="searchAllFeatures"
                />
                <button type="button" class="px-5 py-4 border border-blue-600 rounded-md text-blue-600 flex items-center gap-3 font-bold h-fit" :disabled="exportLoading" @click="exportCSV('allFeatures')">
                    <Icon icon-name="DownloadIcon" />
                    <div>
                        Export CSV
                    </div>
                </button>
            </div>
            <Table 
                :columns="columns" 
                :data="filteredRows"
                :loading="loading"
                sort-mode="internal"
                @rowClick="handleRowClick"
            >
                <template #cell="{ column, row }">
                    <span v-if="column.key === 'tenants_using'">{{ row.tenants_using }}<span class="text-green-800 px-3 py-2 rounded text-sm bg-green-100 ml-2">{{ row.tenant_percentage }}%</span></span>
                    <span v-else-if="column.key === 'total_users_using'">{{ row.total_users_using }}<span class="text-green-800 px-3 py-2 rounded text-sm bg-green-100 ml-2">{{ row.user_percentage }}%</span></span>
                    <span v-else-if="column.key === 'average_monthly_usage'">{{ row.average_monthly_usage.toFixed(2) }}</span>
                    <span v-else-if="column.key === 'last_used_date'">{{ formatLastUsedDate(row && row.last_used_date) }}</span>
                    <span v-else-if="column.key === 'feature_name'">{{ row.feature_name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') }}</span>
                    <span v-else>{{ row[column.key] || '-' }}</span>
                </template>
            </Table>
            <div v-if="pagination && pagination.total_pages > 1" class="flex justify-end pt-4">
                <RamroPagination
                    :current="pagination.current_page"
                    :total="pagination.total_pages"
                    @update="handlePageChange"
                />
            </div>
        </metric-card>
        <metric-card>
            <div class="flex items-center justify-between">
                <div>
                    <h1 class="text-xl font-bold text-gray-900"> Top 5 Most Used Features </h1>
                    <p class="mt-1 text-sm text-gray-600"> Features used by more than 80% of tenants </p>
                </div>
                <FilterDropdown
                    :items="filterItems"
                    label="Top  5 Feature"
                    iconName="DropdownIcon"
                    :itemLabel="item => item.label"
                    :iconReverse="true"
                    @select="selectFeature"
                />
            </div>
            <Loader v-if="chartLoading" />
            <line-chart v-else
                :series="chartSeries" 
                :categories="chartCategories"
                categoryTitle="Months of the year"
                :height="350"
            ></line-chart>
        </metric-card>
        <metric-card class="flex flex-col gap-5">
            <div class="flex justify-between items-center">
                <div class="text-xl font-bold text-gray-800"> Top 10 Active Tenants </div>
                <button type="button" class="px-5 py-4 border border-blue-600 rounded-md text-blue-600 flex items-center gap-3 font-bold h-fit" :disabled="exportLoading" @click="exportCSV('top10ActiveTenants')">
                    <Icon icon-name="DownloadIcon" />
                    <div>
                        Export CSV
                    </div>
                </button>
            </div>
            <Table 
                :columns="tenantColumns" 
                :data="topTenants"
                :loading="tenantLoading"
                sort-mode="internal"
            ></Table>
        </metric-card>
        <metric-card class="flex flex-col gap-5">
            <div class="flex justify-between items-center">
                <div class="text-xl font-bold text-gray-800"> Top 10 Most Recent Tenants </div>
                <button type="button" class="px-5 py-4 border border-blue-600 rounded-md text-blue-600 flex items-center gap-3 font-bold h-fit" :disabled="exportLoading" @click="exportCSV('top10RecentTenants')">
                    <Icon icon-name="DownloadIcon" />
                    <div>
                        Export CSV
                    </div>
                </button>
            </div>
            <Table 
                :columns="recentTenantColumns" 
                :data="recentTenants"
                :loading="recentTenantLoading"
                sort-mode="internal"
            ></Table>
        </metric-card>
        <metric-card class="flex flex-col gap-5">
            <div class="flex justify-between items-center">
                <div class="text-xl font-bold text-gray-800"> Top 5 Most Recent Feature </div>
                <button type="button" class="px-5 py-4 border border-blue-600 rounded-md text-blue-600 flex items-center gap-3 font-bold h-fit" :disabled="exportLoading" @click="exportCSV('top10RecentFeatures')">
                    <Icon icon-name="DownloadIcon" />
                    <div>
                        Export CSV
                    </div>
                </button>
            </div>
            <Table 
                :columns="featureColumns" 
                :data="recentFeatures"
                :loading="recentFeatureLoading"
                sort-mode="internal"
            ></Table>
        </metric-card>
        <div class="flex gap-5">
            <metric-card class="flex flex-col gap-5 w-1/2 bg-red-100 border border-red-300">
                <div class="flex justify-between items-center">
                    <div class="text-xl font-bold text-gray-800"> Top 10 Risky Tenants </div>
                    <button type="button" class="px-5 py-4 border border-blue-600 rounded-md text-blue-600 flex items-center gap-3 font-bold h-fit" :disabled="exportLoading" @click="exportCSV('top10RiskyTenants')">
                        <Icon icon-name="DownloadIcon" />
                        <div>
                            Export CSV
                        </div>
                    </button>
                </div>
                <Table 
                    :columns="riskyTenantColumns" 
                    :data="riskyTenants"
                    :loading="riskyTenantLoading"
                    sort-mode="internal"
                ></Table>
            </metric-card>
            <metric-card class="flex flex-col gap-5 w-1/2 bg-red-100 border border-red-300">
                <div class="flex justify-between items-center">
                    <div class="text-xl font-bold text-gray-800"> Top 10 Risky Features </div>
                    <button type="button" class="px-5 py-4 border border-blue-600 rounded-md text-blue-600 flex items-center gap-3 font-bold h-fit" :disabled="exportLoading" @click="exportCSV('top10RiskyFeatures')">
                        <Icon icon-name="DownloadIcon" />
                        <div>
                            Export CSV
                        </div>
                    </button>
                </div>
                <Table 
                    :columns="riskyFeatureColumns" 
                    :data="riskyFeatures"
                    :loading="riskyFeatureLoading"
                    sort-mode="internal"
                ></Table>
            </metric-card>
        </div>
    </div>
</template>

<style scoped>
.h-fit {
    height: fit-content !important;
}
</style>
